import React, { useCallback, useEffect, useState } from 'react';
import ArticleReader from '@nile/rmm/src/modules/knowledge-base/components/ArticleSearchSheet/ArticleReader';
import {
  Button,
  Heading,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import {
  COMMON_CONSTANTS,
  getTranslatedText,
  NoDataCard,
  SkeletonTableLoading,
  useActivityTracker,
  useCheckFeatureGating
} from '@nile/components';
import { AccountClient, MLClient } from '@nile/graphql';
import {
  GENERATE_SHARE_LINK,
  KB_SUGGESTED_ARTICLE
} from '@nile/rmm/src/modules/knowledge-base/graphql';
import { debounce, isEmpty } from 'lodash';
import { SUGGESTED_ARTICLES } from '@nile/rmm/src/modules/knowledge-base/helper/constants';
import envConfig from '@nile/home/src/Config/env';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
function ArticleSearchSheet({
  toggleKBSheetActive,
  ticketId = null,
  insertLink: insertLinkToParent
}) {
  const intl = useIntl();
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestedArticles, setSuggestedArticles] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const aiFeatureHook = AIFeaturesHook();
  const { getIsAIFeatureEnabled } = aiFeatureHook;

  const isAiKBSearchEnabled =
    useCheckFeatureGating('KNOWLEDGE_BASE')?.enabled &&
    getIsAIFeatureEnabled('KNOWLEDGE_BASE');

  const getArticles = (prefill = false) => {
    setLoading(true);
    let ip = {
      ticketId: ticketId
    };
    if (!isEmpty(searchTerm)) {
      ip.searchTerm = searchTerm;
    }
    useActivityTracker({
      event: 'KB_ARTICLE_SEARCH_SUGGESTION',
      data: {
        ticketId,
        searchTerm
      }
    });
    MLClient.query({
      query: KB_SUGGESTED_ARTICLE,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      variables: ip,
      skip: !ticketId
    })
      .then(async (res) => {
        if (res?.data?.getSuggestedKBArticles) {
          setSuggestedArticles(
            Object.groupBy(
              res?.data?.getSuggestedKBArticles?.articles,
              ({ userType }) => userType
            )
          );
          if (prefill) {
            setSearchText(res?.data?.getSuggestedKBArticles?.searchTerm);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const onSearch = (val) => {
    setSearchTerm(val);
  };
  const filterTable = useCallback(debounce(onSearch, 1000), []);

  const generateUrl = async (variables) => {
    const { data } = await AccountClient.mutate({
      mutation: GENERATE_SHARE_LINK,
      variables: {
        input: {
          kbArticle: {
            itemId: variables?.id
          }
        }
      }
    });
    return data?.generateKbArticleShareLink?.url;
  };
  const insertLink = async (link) => {
    let url = null;
    if (link?.loginRequired) {
      url = `<a href=${window.location.origin}/#/kb/article/${link?.id}/read>${link?.subject}</a>`;
    } else {
      const tokenUrl = await generateUrl(link);
      url = `<a href=${tokenUrl}>${link?.subject}</a>`;
    }
    if (url) {
      useActivityTracker({
        event: 'KB_ARTICLE_INSERT_LINK',
        data: {
          ...link
        }
      });
      insertLinkToParent(url);
    }
    toggleKBSheetActive();
  };

  useEffect(() => {
    if (searchTerm !== null) getArticles();
  }, [searchTerm]);

  useEffect(() => {
    if (ticketId) {
      getArticles(true);
    }
  }, [ticketId]);

  return (
    <div className="w700 bg-white full-height p2040 flex fcol g20">
      {!selectedArticle ? (
        <>
          <div className="flex">
            <Heading element="h1">
              <FormattedMessage id="common.knowledge.base" />
            </Heading>
            <div className="flex f11 jend ptr" onClick={toggleKBSheetActive}>
              <SVGIcon name="Close" />
            </div>
          </div>
          <TextField
            variant="outline"
            placeholder={getTranslatedText(intl, 'app.filter.search.label')}
            prefix={
              <div className="ml10">
                <SVGIcon name="Search" />
              </div>
            }
            onChange={(val) => {
              setSearchText(val);
              filterTable(val);
            }}
            onClearButtonClick={() => {
              setSearchText('');
              filterTable('');
            }}
            clearButton
            value={searchText}
          />
          <TextStyle weight="Medium">
            <FormattedMessage id="app.common.kb.article.search.monica.suggestions" />
          </TextStyle>
          {loading ? (
            <SkeletonTableLoading
              tableHeader={false}
              tableRow={4}
              tableColumn={1}
              borderTop={false}
            />
          ) : (
            <>
              {Object.values(suggestedArticles || {}).every(
                (v) => !v.length
              ) ? (
                <div className="flex-center full-height">
                  <NoDataCard
                    imageURL={`url(${envConfig.imageRoot}svg/articleEmptyResult.svg)`}
                    description={
                      <div className="flex-center fcol g5">
                        <TextStyle weight="Medium">
                          <FormattedMessage id="common.app.kb.article.search.result.empty.title" />
                        </TextStyle>
                        <TextStyle weight="Medium">
                          <FormattedMessage id="common.app.kb.article.search.result.empty.desc" />
                        </TextStyle>
                      </div>
                    }
                  />
                </div>
              ) : (
                <div className="flex fcol g20 scroll h85p">
                  {SUGGESTED_ARTICLES?.map((item) => (
                    <div className="flex g8 fcol">
                      <TextStyle weight="Medium">
                        <FormattedMessage
                          id={item?.label || 'app.noData.message'}
                        />
                      </TextStyle>
                      <div className="flex fcol g5 wrap">
                        {isEmpty(suggestedArticles?.[item?.key]) ? (
                          <div className="flex">
                            <TextStyle weight="Medium" variation="subdued">
                              <FormattedMessage id="app.noData.message" />
                            </TextStyle>
                          </div>
                        ) : (
                          <>
                            {suggestedArticles?.[item?.key]?.map((article) => (
                              <div className="flex mbt5">
                                <div className="flex f11">
                                  <Button
                                    onClick={() =>
                                      setSelectedArticle({
                                        ...article,
                                        userType: item?.key
                                      })
                                    }
                                    icon={
                                      <SVGIcon
                                        name={
                                          article?.visibilityType === 'PUBLIC'
                                            ? 'AllArticle'
                                            : 'PrivateArticle'
                                        }
                                      />
                                    }
                                    plain
                                  >
                                    {article?.subject}
                                  </Button>
                                </div>
                                {!item?.hideInsertButton && (
                                  <div className="flex jend">
                                    <Button
                                      icon={<SVGIcon name="Link" />}
                                      onClick={() => insertLink(article)}
                                      size="slim"
                                    >
                                      {`${getTranslatedText(
                                        intl,
                                        'common.insert'
                                      )} ${getTranslatedText(
                                        intl,
                                        'common.link'
                                      )}`}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <ArticleReader
          selectedArticle={selectedArticle}
          setSelectedArticle={setSelectedArticle}
          closeSheet={toggleKBSheetActive}
          insertLink={insertLink}
        />
      )}
    </div>
  );
}

export default ArticleSearchSheet;
